import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class ResumeDataMapperService {

  constructor() { }

  /**
   * Maps parsed resume data to candidate form fields
   * @param parsedData The parsed resume data
   * @param candidateForm The candidate form to populate
   * @param options Additional options for mapping
   */
  mapResumeDataToForm(parsedData: any, candidateForm: UntypedFormGroup, options: any = {}): void {
    if (!parsedData || !candidateForm) {
      console.error('Invalid data or form provided to mapper');
      return;
    }

    try {
      // Optimized mapping in priority order for faster UX
      // Personal info first - user sees name/email immediately
      this.mapPersonalInfo(parsedData, candidateForm);

      // Summary second - provides immediate value preview
      this.mapProfessionalSummary(parsedData, candidateForm, options);

      // Skills third - most important for job matching
      this.mapSkills(parsedData, candidateForm, options);

      // Experience and education in parallel (async for performance)
      setTimeout(() => {
        this.mapExperience(parsedData, candidateForm);
        this.mapEducation(parsedData, candidateForm);
      }, 0);

      // Secondary data mapping (deferred for performance)
      setTimeout(() => {
        this.mapAdditionalFields(parsedData, candidateForm);
        this.mapWorkPreferences(parsedData, candidateForm);
        this.mapCompensation(parsedData, candidateForm);

        // Final form validation
        candidateForm.markAllAsTouched();
        candidateForm.updateValueAndValidity();
      }, 100);

    } catch (error) {
      console.error('Error mapping resume data:', error);
      // Continue with partial mapping rather than failing completely
      candidateForm.updateValueAndValidity();
    }
  }

  /**
   * Maps personal information fields - Robust for 100+ resume formats
   */
  private mapPersonalInfo(data: any, form: UntypedFormGroup): void {
    // Try multiple possible locations for personal info
    const info = this.findPersonalInfo(data);

    // Parse and set name with multiple fallbacks
    const name = this.findName(info, data);
    if (name) {
      const { firstName, lastName } = this.extractNames(name);
      if (firstName) {
        form.patchValue({
          firstName: firstName,
          lastName: lastName || firstName // Use firstName as lastName if no lastName
        });
      }
    }

    // Set contact information with validation
    const email = this.findEmail(info, data);
    if (email && this.isValidEmail(email)) {
      form.patchValue({
        email: email.toLowerCase(),
        candidateEmailId: email.toLowerCase()
      });
    }

    // Phone with multiple format support
    const phone = this.findPhone(info, data);
    if (phone) {
      const formattedPhone = this.formatPhone(phone);
      if (formattedPhone) {
        form.patchValue({ phone: formattedPhone });
      }
    }

    // Set online profiles with smart detection
    const linkedin = this.findLinkedIn(info, data);
    if (linkedin) {
      form.patchValue({ linkedIn: this.cleanUrl(linkedin) });
    }

    const portfolio = this.findPortfolio(info, data);
    if (portfolio) {
      form.patchValue({ portfolio: this.cleanUrl(portfolio) });
    }

    // Check if resume has ANY location data
    const resumeCity = info.city || data.city || info.City || data.City;
    const resumeState = info.state || data.state || info.State || data.State ||
                        info.province || data.province || info.region || data.region;
    const resumeCountry = info.country || data.country || info.Country || data.Country;
    const resumeZipCode = info.zipCode || info.zipcode || data.zipCode || data.zipcode ||
                          info.zip || data.zip || info.postalCode || data.postalCode;
    const resumeLocation = info.location;

    const hasResumeLocation = resumeCity || resumeState || resumeCountry || resumeZipCode || resumeLocation;

    // If resume has NO location data -> Keep work experience location (don't touch form)
    if (!hasResumeLocation) {
      console.log('Resume has NO location data - keeping work experience location');
      return;
    }

    // Resume HAS location data -> Clear work exp location and use resume location
    console.log('Resume has location data - clearing work exp and setting resume location');
    form.patchValue({
      city: null,
      state: null,
      country: null,
      zipCode: null
    });

    // Parse and set location - check multiple sources
    // First try to parse from location string
    if (resumeLocation) {
      const locationData = this.parseLocation(resumeLocation);
      form.patchValue(locationData);
    }

    // Then check for direct address fields (override parsed values if available)
    if (resumeCity) {
      form.patchValue({ city: resumeCity });
    }

    // Check if "state" is actually a country code (e.g., "IN" for India)
    // Note: Removed US, CA from this list as they are valid US state codes (e.g., CA = California)
    const countryCodesOnly = ['IN', 'AU', 'GB', 'UK'];
    const stateUpper = resumeState?.toUpperCase?.();
    const isStateActuallyCountry = stateUpper && countryCodesOnly.includes(stateUpper) && !resumeCountry;

    if (isStateActuallyCountry) {
      // "state" is actually a country code - use it as country, don't set state
      console.log('Detected state as country code:', stateUpper);
      form.patchValue({ country: stateUpper === 'UK' ? 'GB' : stateUpper });
    } else {
      if (resumeState) {
        form.patchValue({ state: resumeState });
      }

      if (resumeCountry) {
        const mappedCountry = this.mapCountryToCode(resumeCountry);
        if (mappedCountry) {
          form.patchValue({ country: mappedCountry });
        } else if (resumeCountry.length === 2) {
          form.patchValue({ country: resumeCountry.toUpperCase() });
        }
      }
    }

    // Set zipCode (already extracted as resumeZipCode)
    if (resumeZipCode) {
      form.patchValue({ zipCode: resumeZipCode });
    }

    // Check for address
    const address1 = info.address || data.address || info.address1 || data.address1 ||
                     info.street || data.street || info.streetAddress || data.streetAddress;
    if (address1) {
      form.patchValue({ address1: address1 });
    }

    const address2 = info.address2 || data.address2 || info.addressLine2 || data.addressLine2;
    if (address2) {
      form.patchValue({ address2: address2 });
    }
  }

  /**
   * Maps professional summary with enhancement
   */
  private mapProfessionalSummary(data: any, form: UntypedFormGroup, options: any): void {
    let summary = data.summary || '';

    // Enhance summary with additional context if available
    if (options.enhanceSummary) {
      summary = this.enhanceSummary(data, summary);
    }

    if (summary) {
      // Build structured JD-like HTML for Summernote
      const structuredHtml = this.buildStructuredSummaryHtml(data, summary);

      form.patchValue({
        resumeSummary: structuredHtml,
        description: structuredHtml
      });

      // Force update summernote if present
      this.updateSummernote('resumeSummary', structuredHtml);
    }
  }

  private buildStructuredSummaryHtml(data: any, fallbackSummary: string): string {
    let html = '';
    const cleanSummary = fallbackSummary.replace(/\*\*(.*?)\*\*/g, '$1').trim();

    // 1. ROLE — try multiple fallbacks
    const roleTitle = data.job_title_match?.current_title_match?.current_title
      || (fallbackSummary ? (fallbackSummary.match(/\*\*(.*?)\*\*/) || [])[1] : null)
      || this.getCurrentJobTitle(data)
      || (data.experience?.[0]?.title || '')
      || this.extractRoleFromSummary(cleanSummary);
    if (roleTitle) {
      html += `<p><strong>Title:</strong> ${roleTitle}</p>`;
    }

    // 2. TOTAL EXPERIENCE
    const totalExp = data.ranking_metrics?.total_experience_years
      || this.extractExperienceYears(cleanSummary);
    if (totalExp) {
      html += `<p><strong>Total Experience:</strong> ${totalExp} year${totalExp === 1 ? '' : 's'}</p>`;
    }

    // 3. DOMAIN EXPERTISE — from summary inline text or dedicated field
    const domain = this.extractInlineLabel(cleanSummary, ['Primary domain expertise', 'Domain expertise', 'Domain']);
    if (domain) {
      html += `<p><strong>Domain Expertise:</strong></p><p>${domain}</p>`;
    }

    // 4. CORE TECHNICAL STACK — from summary inline
    const coreStack = this.extractInlineLabel(cleanSummary, ['Core technical stack', 'Technical stack']);
    if (coreStack) {
      html += `<p><strong>Core Technical Stack:</strong></p><p>${coreStack}</p>`;
    }

    // 5. KEY SPECIALIZATIONS — from summary inline
    const specializations = this.extractInlineLabel(cleanSummary, ['Key specializations', 'Specializations']);
    if (specializations) {
      html += `<p><strong>Key Specializations:</strong></p><p>${specializations}</p>`;
    }

    // 6. CAREER FOCUS — from summary inline
    const careerFocus = this.extractInlineLabel(cleanSummary, ['Career focus', 'Career objective']);
    if (careerFocus) {
      html += `<p><strong>Career Focus:</strong></p><p>${careerFocus}</p>`;
    }

    // 7. RESPONSIBILITIES — collect from all experiences
    const allResponsibilities: string[] = (data.experience || [])
      .flatMap((exp: any) => exp.responsibilities || [])
      .filter((r: string, i: number, arr: string[]) => r && arr.indexOf(r) === i);
    if (allResponsibilities.length > 0) {
      html += `<p><strong>Responsibilities:</strong></p><ul>`;
      allResponsibilities.slice(0, 10).forEach((resp: string) => {
        html += `<li>${resp}</li>`;
      });
      html += `</ul>`;
    }

    // 8. WORK EXPERIENCE — each entry separately
    if (data.experience && data.experience.length > 0) {
      html += `<p><strong>Work Experience:</strong></p>`;
      data.experience.forEach((exp: any) => {
        const title = exp.title || exp.position || '';
        const company = exp.company || exp.organization || '';
        const start = exp.start_date || '';
        const end = exp.end_date || 'Present';
        const location = exp.location || '';

        html += `<p><strong>${title}${company ? ' — ' + company : ''}`;
        if (start) html += ` (${start} – ${end})`;
        if (location) html += ` | ${location}`;
        html += `</strong></p>`;

        const expResponsibilities: string[] = exp.responsibilities || [];
        if (expResponsibilities.length > 0) {
          html += `<ul>`;
          expResponsibilities.slice(0, 5).forEach((resp: string) => {
            html += `<li>${resp}</li>`;
          });
          html += `</ul>`;
        }

        const expAchievements: string[] = exp.achievements || [];
        if (expAchievements.length > 0) {
          html += `<p><em>Achievements:</em></p><ul>`;
          expAchievements.slice(0, 3).forEach((ach: string) => {
            html += `<li>${ach}</li>`;
          });
          html += `</ul>`;
        }
      });
    }

    // 9. TECHNICAL SKILLS
    const techSkills = [
      ...(data.skills?.technical_skills || []),
      ...(data.skills?.frameworks || []),
      ...(data.skills?.tools || []),
      ...(data.skills?.databases || [])
    ].filter((s: string, i: number, arr: string[]) => s && arr.indexOf(s) === i);
    if (techSkills.length > 0) {
      html += `<p><strong>Technical Skills:</strong></p><p>${techSkills.join(', ')}</p>`;
    }

    // 10. LANGUAGES
    if (data.skills?.languages?.length > 0) {
      html += `<p><strong>Languages:</strong></p><p>${data.skills.languages.join(', ')}</p>`;
    }

    // 11. SOFT SKILLS
    if (data.skills?.soft_skills?.length > 0) {
      html += `<p><strong>Soft Skills:</strong></p><p>${data.skills.soft_skills.join(', ')}</p>`;
    }

    // 12. CERTIFICATIONS
    if (data.certifications?.length > 0) {
      const certNames = data.certifications
        .map((c: any) => typeof c === 'object' ? c.name : c)
        .filter(Boolean);
      if (certNames.length > 0) {
        html += `<p><strong>Certifications:</strong></p><p>${certNames.join(', ')}</p>`;
      }
    }

    return html || `<p>${cleanSummary}</p>`;
  }

  /** Title abbreviations that carry a dot without ending the sentence ("Sr.", "Dr.") */
  private readonly ROLE_ABBREVIATION = /(?:^|\s)(?:sr|jr|dr|mr|mrs|ms|st|ph|inc|ltd|co|corp)$/i;

  /** Extracts the job title from the beginning of a summary string */
  private extractRoleFromSummary(summary: string): string {
    if (!summary) return '';

    // The backend summary is newline-delimited; the role is always on line 1.
    let role = summary.split(/\r?\n/)[0].trim()
      .replace(/\s+with\s+[\d.].*$/i, '')   // "... with 10.5 years of total experience"
      .replace(/\s+at\s+.*$/i, '')          // "... at Acme Corp"
      .replace(/\s*[,;:–—-]\s*$/, '')
      .trim();

    // Keep going past abbreviation dots ("Sr.", "Jr.", "Dr.") before cutting the sentence.
    const parts = role.split('. ');
    role = parts[0];
    for (let i = 1; i < parts.length && this.ROLE_ABBREVIATION.test(role); i++) {
      role += '. ' + parts[i];
    }
    return role.trim();
  }

  /** Extracts total years of experience from summary text */
  private extractExperienceYears(summary: string): number | null {
    if (!summary) return null;
    const match = summary.match(/(\d+(?:\.\d+)?)\s*(?:\+\s*)?years?\s+(?:of\s+)?(?:total\s+)?experience/i);
    return match ? parseFloat(match[1]) : null;
  }

  /** Extracts value after a label pattern like "Label: value. Next" */
  private extractInlineLabel(summary: string, labels: string[]): string {
    if (!summary) return '';
    for (const label of labels) {
      const regex = new RegExp(`${label}\\s*[:\\-]\\s*([^.]+(?:\\.[^A-Z][^.]+)*)`, 'i');
      const match = summary.match(regex);
      if (match) {
        return match[1].trim().replace(/\s+/g, ' ');
      }
    }
    return '';
  }

  /**
   * Maps and prioritizes skills based on job title
   */
  private mapSkills(data: any, form: UntypedFormGroup, options: any): void {
    let primarySkills: string[] = [];
    let secondarySkills: string[] = [];
    let allSkills: string[] = [];

    console.log('=== SKILL MAPPING DEBUG ===');
    console.log('Data received:', data);
    console.log('skills.primary_skills:', data.skills?.primary_skills);
    console.log('skills.secondary_skills:', data.skills?.secondary_skills);

    // PRIORITY 1: Use primary_skills and secondary_skills from OpenAI if available
    if (data.skills?.primary_skills && Array.isArray(data.skills.primary_skills) && data.skills.primary_skills.length > 0) {
      console.log('Using primary_skills and secondary_skills from OpenAI API');
      primarySkills = data.skills.primary_skills;
      secondarySkills = data.skills.secondary_skills || [];
      allSkills = [...primarySkills, ...secondarySkills];
    }
    // PRIORITY 2: Fallback to old logic
    else {
      console.log('Falling back to extractAllSkills logic');
      allSkills = this.extractAllSkills(data);

      if (allSkills.length === 0) return;

      // Get current job title for skill prioritization
      const jobTitle = this.getCurrentJobTitle(data);

      // Prioritize skills based on job title
      const prioritizedSkills = this.prioritizeSkillsByRole(allSkills, jobTitle);

      // Increased limits to show more skills
      primarySkills = prioritizedSkills.slice(0, 8);      // Increased from 3 to 8
      secondarySkills = prioritizedSkills.slice(8, 16);   // Increased from 6 to 16
    }

    console.log('Final skills to set:');
    console.log('Primary skills:', primarySkills);
    console.log('Secondary skills:', secondarySkills);

    // Update form with skills
    if (primarySkills.length > 0) {
      form.patchValue({ primarySkills: primarySkills });

      // Update skill lists if provided
      if (options.primarySkillsList) {
        this.updateSkillsList(primarySkills, options.primarySkillsList);
      }
    }

    if (secondarySkills.length > 0) {
      form.patchValue({ secondarySkills: secondarySkills });

      if (options.secondarySkillsList) {
        this.updateSkillsList(secondarySkills, options.secondarySkillsList);
      }
    }

    // Also try to populate any other skill fields
    if (allSkills.length > 0) {
      const allSkillsString = allSkills.join(', ');
      if (form.get('skills')) {
        form.patchValue({ skills: allSkillsString });
      }
      if (form.get('technicalSkills')) {
        form.patchValue({ technicalSkills: allSkillsString });
      }
    }
  }

  /**
   * Maps work experience
   */
  private mapExperience(data: any, form: UntypedFormGroup): void {
    if (!data.experience || data.experience.length === 0) return;
    
    const latestExperience = data.experience[0];
    
    // Calculate total experience
    const totalYears = this.calculateTotalExperience(data.experience);
    if (totalYears > 0) {
      form.patchValue({
        totalExperience: totalYears.toString()
      });
    }
    
    // Set current position details
    if (latestExperience.company) {
      form.patchValue({
        currentEmployer: latestExperience.company
      });
    }
    
    if (latestExperience.position) {
      form.patchValue({
        currentJobTitle: latestExperience.position,
        jobTitle: latestExperience.position
      });
    }
  }

  /**
   * Maps education information
   */
  private mapEducation(data: any, form: UntypedFormGroup): void {
    if (!data.education || data.education.length === 0) return;
    
    const highestEducation = data.education[0];
    if (highestEducation.degree) {
      form.patchValue({
        qualification: this.mapDegreeToQualification(highestEducation.degree)
      });
    }
    
    if (highestEducation.institution) {
      form.patchValue({
        institution: highestEducation.institution
      });
    }
    
    if (highestEducation.field_of_study) {
      form.patchValue({
        fieldOfStudy: highestEducation.field_of_study
      });
    }
  }

  /**
   * Maps additional candidate fields
   */
  private mapAdditionalFields(data: any, form: UntypedFormGroup): void {
    // Map languages
    if (data.languages && data.languages.length > 0) {
      const languages = data.languages.map(lang => 
        typeof lang === 'object' ? lang.name : lang
      );
      
      const currentNotes = form.get('additionalNotes')?.value || '';
      form.patchValue({
        additionalNotes: currentNotes + (currentNotes ? '\n' : '') + 'Languages: ' + languages.join(', ')
      });
    }
    
    // Map certifications count
    if (data.certifications && data.certifications.length > 0) {
      form.patchValue({
        certificationsCount: data.certifications.length
      });
    }
    
    // Map awards
    if (data.awards && data.awards.length > 0) {
      const currentSummary = form.get('resumeSummary')?.value || '';
      if (!currentSummary.includes('Awards')) {
        form.patchValue({
          resumeSummary: currentSummary + '\n\nAwards & Recognition: ' + data.awards.join(', ')
        });
      }
    }
  }

  /**
   * Maps work preferences based on parsed data
   */
  private mapWorkPreferences(data: any, form: UntypedFormGroup): void {
    // Detect work authorization based on location
    const workAuth = this.detectWorkAuthorization(data);
    if (workAuth) {
      form.patchValue({ workAuthorization: workAuth });
    }
    
    // Set engagement type based on experience
    const engagementType = this.determineEngagementType(data);
    if (engagementType && engagementType != '') {
      form.patchValue({ engagementType: engagementType });
    }
    
    // Work preferences - check resume first, then use defaults
    const personalInfo = this.findPersonalInfo(data);

    // Availability - check resume first
    if (personalInfo.availability !== undefined && personalInfo.availability !== null) {
      form.patchValue({ availability: personalInfo.availability });
    } else {
      form.patchValue({ availability: true }); // Default
    }

    // Available In - check resume first
    if (personalInfo.availableIn) {
      form.patchValue({ availableIn: personalInfo.availableIn });
    } else {
      form.patchValue({ availableIn: 'Immediate' }); // Default
    }

    // Remote Work - check resume first
    if (personalInfo.remoteWork !== undefined && personalInfo.remoteWork !== null) {
      form.patchValue({ remoteWork: personalInfo.remoteWork });
    } else {
      form.patchValue({ remoteWork: true }); // Default
    }

    // Work From Home - check resume first
    if (personalInfo.workFromHome !== undefined && personalInfo.workFromHome !== null) {
      form.patchValue({ workFromHome: personalInfo.workFromHome });
    } else {
      form.patchValue({ workFromHome: true }); // Default
    }

    // Relocation Required - check resume first
    if (personalInfo.relocationRequired !== undefined && personalInfo.relocationRequired !== null) {
      form.patchValue({ relocationRequired: personalInfo.relocationRequired });
    } else {
      form.patchValue({ relocationRequired: false }); // Default
    }

    // Work Mode - check resume first
    if (personalInfo.workMode) {
      form.patchValue({ workMode: personalInfo.workMode });
    } else {
      form.patchValue({ workMode: 'Hybrid' }); // Default
    }
    
    // Check for security clearance mentions
    const resumeText = JSON.stringify(data).toLowerCase();
    form.patchValue({
      securityClearance: this.hasSecurityClearance(resumeText)
    });
  }

  /**
   * Maps compensation expectations - ONLY use parsed values from resume, no auto-calculation
   */
  private mapCompensation(data: any, form: UntypedFormGroup): void {
    const personalInfo = this.findPersonalInfo(data);

    // Only set if actually parsed from resume (not calculated)
    if (personalInfo.targetRateFrom) {
      form.patchValue({ targetRateFrom: personalInfo.targetRateFrom });
    }
    if (personalInfo.targetRateTo) {
      form.patchValue({ targetRateTo: personalInfo.targetRateTo });
    }
    if (personalInfo.payType) {
      form.patchValue({ payType: this.normalizePayType(personalInfo.payType) });
    }
    // If not found, fields remain NULL - triggers validation error
  }

  /**
   * Normalize payType to match UI expected values (Title Case)
   * API may return lowercase values like "hourly", UI expects "Hourly"
   */
  private normalizePayType(payType: string): string {
    if (!payType) return null;
    const normalized = payType.toLowerCase();
    // Valid values: Hourly, Daily, Weekly, Monthly, Yearly
    if (normalized === 'hourly' || normalized === 'hr') return 'Hourly';
    if (normalized === 'daily') return 'Daily';
    if (normalized === 'weekly') return 'Weekly';
    if (normalized === 'monthly') return 'Monthly';
    if (normalized === 'yearly' || normalized === 'annual') return 'Yearly';
    return payType; // Return as-is if not recognized
  }

  // Helper Methods

  private extractNames(fullName: string): { firstName: string; lastName: string } {
    if (!fullName) return { firstName: '', lastName: '' };
    
    // Clean name from special characters and extra spaces
    const cleanName = fullName
      .replace(/[^a-zA-Z\s\-']/g, '') // Keep only letters, spaces, hyphens, apostrophes
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
    
    const parts = cleanName.split(' ').filter(p => p);
    
    // Handle various name formats
    if (parts.length === 0) {
      return { firstName: '', lastName: '' };
    } else if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    } else if (parts.length === 2) {
      return { firstName: parts[0], lastName: parts[1] };
    } else {
      // For 3+ parts, first is firstName, rest is lastName
      return {
        firstName: parts[0],
        lastName: parts.slice(1).join(' ')
      };
    }
  }

  private parseLocation(location: string): any {
    const locationParts = location.split(',').map(part => part.trim());
    const result: any = {};
    
    if (locationParts.length >= 1) {
      result.city = locationParts[0];
    }
    
    if (locationParts.length >= 2) {
      result.state = locationParts[1];
    }
    
    if (locationParts.length >= 3) {
      const country = locationParts[2];
      result.country = this.mapCountryToCode(country);
    }
    
    return result;
  }

  private mapCountryToCode(country: string): string {
    if (!country) return ''; // No default - let validation catch it

    const countryLower = country.toLowerCase();

    if (countryLower.includes('usa') || countryLower.includes('united states') || countryLower.includes('america')) {
      return 'US';
    } else if (countryLower.includes('india')) {
      return 'IN';
    } else if (countryLower.includes('canada')) {
      return 'CA';
    } else if (countryLower.includes('australia')) {
      return 'AU';
    } else if (countryLower.includes('uk') || countryLower.includes('united kingdom') || countryLower.includes('britain')) {
      return 'GB';
    }

    // If country string is already a 2-letter code, return it
    if (country.length === 2) {
      return country.toUpperCase();
    }

    return ''; // No default - let validation catch it
  }

  private extractAllSkills(data: any): string[] {
    const allSkills = [];

    console.log('Raw resume data for skill extraction:', JSON.stringify(data, null, 2));

    if (data.skills) {
      const skills = data.skills;

      // Priority order: technical > frameworks > databases > tools > programming languages > soft skills
      if (skills.technical_skills) allSkills.push(...skills.technical_skills);
      if (skills.frameworks) allSkills.push(...skills.frameworks);
      if (skills.databases) allSkills.push(...skills.databases);
      if (skills.tools) allSkills.push(...skills.tools);
      if (skills.programming_languages) allSkills.push(...skills.programming_languages);
      if (skills.languages) allSkills.push(...skills.languages);
      if (skills.soft_skills) allSkills.push(...skills.soft_skills);
    }

    // Also check root level and various skill field names
    if (data.technical_skills) allSkills.push(...data.technical_skills);
    if (data.programming_languages) allSkills.push(...data.programming_languages);
    if (data.frameworks) allSkills.push(...data.frameworks);
    if (data.tools) allSkills.push(...data.tools);
    if (data.databases) allSkills.push(...data.databases);
    if (data.primarySkills) allSkills.push(...data.primarySkills);
    if (data.secondarySkills) allSkills.push(...data.secondarySkills);
    if (data.technologies) allSkills.push(...data.technologies);

    // Check nested experience for additional skills
    if (data.experience && Array.isArray(data.experience)) {
      data.experience.forEach(exp => {
        if (exp.skills) allSkills.push(...exp.skills);
        if (exp.technologies) allSkills.push(...exp.technologies);
        if (exp.tools) allSkills.push(...exp.tools);
      });
    }

    // Remove duplicates, empty values, and normalize
    const uniqueSkills: string[] = [];
    const seen = new Set<string>();

    allSkills
      .filter(skill => skill && skill.trim() && skill.trim().length > 1)
      .forEach(skill => {
        const normalizedSkill = skill.trim();
        const lowerSkill = normalizedSkill.toLowerCase();

        // Avoid very generic terms
        if (!['good', 'excellent', 'strong', 'basic', 'advanced'].includes(lowerSkill)) {
          if (!seen.has(lowerSkill)) {
            seen.add(lowerSkill);
            uniqueSkills.push(normalizedSkill);
          }
        }
      });

    console.log('Final extracted unique skills:', uniqueSkills);
    return uniqueSkills;
  }

  private getCurrentJobTitle(data: any): string {
    if (data.experience && data.experience.length > 0) {
      return data.experience[0].position || '';
    }
    return '';
  }

  private prioritizeSkillsByRole(skills: string[], jobTitle: string): string[] {
    if (!skills || skills.length === 0) return skills;
    
    const titleLower = jobTitle.toLowerCase();
    
    // Role-based skill priorities
    const rolePriorities = this.getRolePriorities(titleLower);
    
    // Sort skills by priority
    return skills.sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      
      const aPriority = rolePriorities.findIndex(keyword => aLower.includes(keyword));
      const bPriority = rolePriorities.findIndex(keyword => bLower.includes(keyword));
      
      if (aPriority === -1 && bPriority === -1) return 0;
      if (aPriority === -1) return 1;
      if (bPriority === -1) return -1;
      
      return aPriority - bPriority;
    });
  }

  private getRolePriorities(titleLower: string): string[] {
    // Define role-specific skill priorities
    const priorities: { [key: string]: string[] } = {
      java: ['java', 'spring', 'springboot', 'hibernate', 'maven', 'gradle', 'microservices'],
      frontend: ['react', 'angular', 'vue', 'javascript', 'typescript', 'html', 'css', 'sass'],
      backend: ['nodejs', 'python', 'java', 'spring', 'django', 'express', 'api', 'database'],
      fullstack: ['javascript', 'typescript', 'react', 'nodejs', 'mongodb', 'postgresql', 'docker'],
      devops: ['docker', 'kubernetes', 'jenkins', 'aws', 'azure', 'terraform', 'ci/cd', 'linux'],
      data: ['python', 'sql', 'pandas', 'numpy', 'spark', 'hadoop', 'tableau', 'machine learning'],
      mobile: ['react native', 'flutter', 'swift', 'kotlin', 'ios', 'android', 'firebase']
    };
    
    // Determine which priority list to use
    for (const [key, values] of Object.entries(priorities)) {
      if (titleLower.includes(key)) {
        return values;
      }
    }
    
    // Default priority for general developers
    return priorities.fullstack;
  }

  private calculateTotalExperience(experiences: any[]): number {
    if (!experiences || experiences.length === 0) return 0;
    
    let totalMonths = 0;
    
    experiences.forEach(exp => {
      if (exp.start_date) {
        const startDate = new Date(exp.start_date);
        const endDate = exp.end_date && exp.end_date !== 'Present' 
          ? new Date(exp.end_date) 
          : new Date();
        
        const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 
          + (endDate.getMonth() - startDate.getMonth());
        
        totalMonths += Math.max(0, months); // Ensure non-negative
      }
    });
    
    return Math.round(totalMonths / 12);
  }

  private mapDegreeToQualification(degree: string): string {
    if (!degree) return '';
    
    const degreeLower = degree.toLowerCase();
    
    const mappings: { [key: string]: string[] } = {
      'Doctorate': ['phd', 'doctorate', 'doctoral'],
      'Master': ['master', 'mba', 'msc', 'ma', 'mca', 'm.tech', 'ms', 'meng'],
      'Bachelor': ['bachelor', 'bsc', 'ba', 'bba', 'b.tech', 'be', 'beng', 'bs'],
      'Associate': ['associate', 'as', 'aa', 'aas'],
      'Diploma': ['diploma', 'certificate']
    };
    
    for (const [qualification, keywords] of Object.entries(mappings)) {
      if (keywords.some(keyword => degreeLower.includes(keyword))) {
        return qualification;
      }
    }
    
    return degree; // Return as-is if no mapping found
  }

  private detectWorkAuthorization(data: any): string {
    const location = data.personal_info?.location?.toLowerCase() || '';
    const resumeText = JSON.stringify(data).toLowerCase();
    
    // Check for explicit mentions
    if (resumeText.includes('us citizen') || resumeText.includes('citizen')) {
      return 'US Citizen';
    } else if (resumeText.includes('green card') || resumeText.includes('permanent resident')) {
      return 'Green Card';
    } else if (resumeText.includes('h1b') || resumeText.includes('h-1b')) {
      return 'H1B';
    } else if (resumeText.includes('opt') || resumeText.includes('cpt')) {
      return 'OPT/CPT';
    }
    
    // Default based on location
    if (location.includes('usa') || location.includes('united states')) {
      return 'US Citizen';
    } else if (location.includes('india')) {
      return 'H1B';
    }
    
    return 'Other';
  }

  private determineEngagementType(data: any): string {
    const totalYears = this.calculateTotalExperience(data.experience || []);
    const resumeText = JSON.stringify(data).toLowerCase();
    
    // Check for explicit mentions
    if (resumeText.includes('contractor') || resumeText.includes('consultant')) {
      // return 'Contract';
      return 'W2 - Contract';
    } else if (resumeText.includes('full time') || resumeText.includes('full-time')) {
      // return 'Full Time';
      return 'W2 - Full Time';
    } else if (resumeText.includes('part time') || resumeText.includes('part-time')) {
      return 'Part Time';
    }
    
    // Based on experience
    if (totalYears >= 10) {
      return 'W2 - Full Time';
    } else if (totalYears >= 5) {
      return 'W2 - Contract';
    }
    
    return '';
  }

  private hasSecurityClearance(resumeText: string): boolean {
    const clearanceKeywords = [
      'security clearance',
      'secret clearance',
      'top secret',
      'ts/sci',
      'public trust',
      'confidential clearance'
    ];
    
    return clearanceKeywords.some(keyword => resumeText.includes(keyword));
  }

  private calculateTargetRate(totalYears: number, jobTitle: string): { rate: number; payType: string } {
    let baseRate = 50; // Base hourly rate
    
    // Adjust based on experience
    if (totalYears >= 20) {
      baseRate = 180;
    } else if (totalYears >= 15) {
      baseRate = 150;
    } else if (totalYears >= 10) {
      baseRate = 120;
    } else if (totalYears >= 7) {
      baseRate = 100;
    } else if (totalYears >= 5) {
      baseRate = 80;
    } else if (totalYears >= 3) {
      baseRate = 60;
    }
    
    // Adjust for seniority in title
    const titleLower = jobTitle.toLowerCase();
    
    if (titleLower.includes('chief') || titleLower.includes('vp') || titleLower.includes('vice president')) {
      baseRate += 50;
    } else if (titleLower.includes('director') || titleLower.includes('head')) {
      baseRate += 40;
    } else if (titleLower.includes('manager') || titleLower.includes('lead')) {
      baseRate += 30;
    } else if (titleLower.includes('senior') || titleLower.includes('principal') || titleLower.includes('architect')) {
      baseRate += 20;
    }
    
    // Adjust for specialized roles
    if (titleLower.includes('ai') || titleLower.includes('machine learning') || titleLower.includes('data scientist')) {
      baseRate += 30;
    } else if (titleLower.includes('blockchain') || titleLower.includes('security')) {
      baseRate += 25;
    } else if (titleLower.includes('cloud') || titleLower.includes('devops')) {
      baseRate += 20;
    }
    
    return {
      rate: baseRate,
      payType: 'Hourly'
    };
  }

  private enhanceSummary(data: any, baseSummary: string): string {
    // If we have a good base summary, use it as foundation
    if (baseSummary && baseSummary.length > 50) {
      return baseSummary; // Trust the AI-generated summary
    }

    // Quick return if data is insufficient for enhancement
    if (!data || Object.keys(data).length < 3) {
      return baseSummary || 'Experienced professional ready to contribute to team success.';
    }

    try {
      // Calculate key metrics
      const totalYears = this.calculateTotalExperience(data.experience || []);
      const companies = data.experience?.slice(0, 3)?.map(exp => exp.company).filter(c => c) || [];
      const topSkills = this.extractTopSkills(data);
      const currentRole = data.experience?.[0]?.position || '';
      const currentCompany = data.experience?.[0]?.company || '';
      const education = data.education?.[0];
      const certifications = data.certifications || [];

      // Build comprehensive 5+ line summary as requested
      const summaryLines = [];

      // Line 1: Professional identity and experience
      if (currentRole && totalYears > 0) {
        summaryLines.push(`${currentRole} with ${totalYears}+ years of professional experience in software development and technology.`);
      } else if (totalYears > 0) {
        summaryLines.push(`Experienced software professional with ${totalYears}+ years in the technology industry.`);
      } else {
        summaryLines.push(`Dedicated software developer with strong technical expertise and proven problem-solving abilities.`);
      }

      // Line 2: Current role and company context
      if (currentCompany && currentRole) {
        summaryLines.push(`Currently serving as ${currentRole} at ${currentCompany}, contributing to innovative software solutions and technical excellence.`);
      } else if (companies.length > 0) {
        summaryLines.push(`Proven track record working with ${companies.length} organizations including ${companies.slice(0, 2).join(' and ')}.`);
      } else {
        summaryLines.push(`Strong background in software development with focus on delivering high-quality solutions.`);
      }

      // Line 3: Technical skills and expertise (most important for matching)
      if (topSkills.length > 0) {
        summaryLines.push(`Expertise in ${topSkills.slice(0, 8).join(', ')} with strong foundation in modern development practices and frameworks.`);
      } else {
        summaryLines.push(`Strong technical foundation in software development, programming languages, and modern development frameworks.`);
      }

      // Line 4: Education and qualifications
      if (education?.degree && education?.institution) {
        summaryLines.push(`${education.degree} graduate from ${education.institution} with solid academic foundation in computer science and engineering.`);
      } else if (education?.degree) {
        summaryLines.push(`${education.degree} graduate with strong educational background in technology and software development.`);
      } else {
        summaryLines.push(`Continuous learner with commitment to staying current with latest technologies and industry best practices.`);
      }

      // Line 5: Professional achievements and value proposition
      const achievements = data.experience?.flatMap(exp => exp.achievements || []).filter(a => a) || [];
      if (achievements.length > 0) {
        summaryLines.push(`Demonstrated success in project delivery, team collaboration, and technical innovation with measurable business impact.`);
      } else {
        summaryLines.push(`Strong analytical and problem-solving skills with passion for creating efficient, scalable, and maintainable software solutions.`);
      }

      // Line 6: Additional credentials or career focus
      if (certifications.length > 0) {
        summaryLines.push(`Professional development includes ${certifications.length} certifications, demonstrating commitment to continuous learning and industry excellence.`);
      } else {
        summaryLines.push(`Ready to contribute technical expertise and collaborative spirit to drive innovation and achieve organizational goals.`);
      }

      return summaryLines.join(' ');

    } catch (error) {
      console.error('Error enhancing summary:', error);
      return baseSummary || 'Experienced professional ready to contribute to organizational success.';
    }
  }

  private extractTopSkills(data: any): string[] {
    const allSkills = [];

    if (data.skills?.technical_skills) {
      allSkills.push(...data.skills.technical_skills);
    }
    if (data.skills?.frameworks) {
      allSkills.push(...data.skills.frameworks);
    }
    if (data.skills?.languages) {
      allSkills.push(...data.skills.languages);
    }
    if (data.skills?.tools) {
      allSkills.push(...data.skills.tools);
    }

    // Return unique skills, prioritizing common tech skills
    const uniqueSkills = [...new Set(allSkills)];
    const prioritySkills = ['Angular', 'React', 'JavaScript', 'TypeScript', 'Java', 'Python', 'AWS', 'Spring Boot'];

    const sorted = uniqueSkills.sort((a, b) => {
      const aIndex = prioritySkills.findIndex(skill => a.toLowerCase().includes(skill.toLowerCase()));
      const bIndex = prioritySkills.findIndex(skill => b.toLowerCase().includes(skill.toLowerCase()));

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return 0;
    });

    return sorted.slice(0, 8);
  }

  private updateSkillsList(skills: string[], targetList: any[]): void {
    skills.forEach(skill => {
      if (!targetList.includes(skill)) {
        targetList.push(skill);
      }
    });
  }

  private updateSummernote(fieldName: string, content: string): void {
    // Update summernote editor if present
    setTimeout(() => {
      if ((window as any).$ && (window as any).$(`[formControlName="${fieldName}"]`).length > 0) {
        (window as any).$(`[formControlName="${fieldName}"]`).summernote('code', content);
      }
    }, 100);
  }

  /**
   * Formats work experience for saving to backend
   */
  formatWorkExperienceForBackend(experience: any, candidateId: string, index: number,countries: any): any {
    const startParts = this.parseDateString(experience.start_date);
    const endParts = experience.end_date && experience.end_date !== 'Present'
      ? this.parseDateString(experience.end_date)
      : null;

     // Extract location data from experience if available
     // Extract location data from experience if available
    const expLocation = experience.location || '';
    let city = '';
    let state = '';
    let country = '';
    let zipcode = '';
 
    // Only populate location if it exists in the experience data
    if (expLocation && expLocation.trim()) {
      const locationParts = expLocation.split(',').map((part: string) => part.trim());
 
      if (locationParts.length >= 1) {
        city = locationParts[0] || '';
      }
      if (locationParts.length >= 2) {
        state = locationParts[1] || '';
      }
      if (locationParts.length >= 3) {
        country = this.mapCountryToCode(locationParts[2]) || '';
      }
    }

    city = experience.city;
    state = experience.state;
    
    let expCountry = experience.country;
    if (expCountry?.trim() && countries?.length) {
      expCountry = expCountry.toLowerCase();
      country = countries.find(
          c => c.countryCode.toLowerCase() === expCountry || c.countryName.toLowerCase() === expCountry
        )?.countryCode ?? expCountry;
    } else {
      country = expCountry;
    }

    // Match EXACTLY with CandidateWorkExperience entity fields and types
    return {
      expId: null,
      organisationName: experience.company || '',
      currentOrganization: index === 0, // boolean
      title: experience.position || '',
      startMonth: startParts.month,
      startYear: startParts.year ? String(startParts.year) : '', // Ensure String type
      endMonth: endParts?.month || null,
      endYear: endParts?.year ? String(endParts.year) : null, // Ensure String type
      street: null,
      city: city, // Only populated if location exists in experience
      state: state, // Only populated if location exists in experience
      zipcode: zipcode, // Only populated if location exists in experience
      country: country, // Only populated if location exists in experience (not defaulted to 'US')
      badge: false, // boolean, not null
      previousUserTpe: null,
      clientType: 'Client',
      candidateId: candidateId,
      streamAction: 'ADD'
    };
  }

  /**
   * Formats education for saving to backend
   */
  formatEducationForBackend(education: any, candidateId: string, index: number = 0, allEducation: any[] = [], firstJobYear: number | null = null): any {
    console.log('formatEducationForBackend called with:', {
      education,
      index,
      firstJobYear
    });
    
    // Initialize empty dates
    let startParts = { month: '', year: '' };
    let endParts = { month: '', year: '' };
    
    // If we have first job year, ALWAYS calculate backwards for education
    // This ensures proper sequencing
    if (firstJobYear) {
      console.log('Calculating education dates from first job year:', firstJobYear);
      const estimatedDates = this.calculateEducationDatesFromJobStart(education, firstJobYear, index, allEducation);
      console.log('Calculated dates:', estimatedDates);
      startParts = estimatedDates.start;
      endParts = estimatedDates.end;
    } else {
      console.log('No first job year, trying to parse dates from education data');
      // Only use parsed dates if no job year is available
      // and the dates look reasonable
      if (education.start_date) {
        const parsed = this.parseDateString(education.start_date);
        console.log('Parsed start date:', parsed);
        // Only use if year is reasonable and not in future
        if (parsed.year && parseInt(parsed.year) <= new Date().getFullYear() && parseInt(parsed.year) > 1950) {
          startParts = parsed;
        }
      }
      
      if (education.end_date) {
        const parsed = this.parseDateString(education.end_date);
        console.log('Parsed end date:', parsed);
        // Only use if year is reasonable
        if (parsed.year && parseInt(parsed.year) <= new Date().getFullYear() + 1 && parseInt(parsed.year) > 1950) {
          endParts = parsed;
        }
      }
    }
    
    console.log('Final date parts before validation:', { startParts, endParts });
    
    // If still no dates, return with null values (don't show invalid dates)
    // IMPORTANT: Do not update year fields if not available as per user request
    if (!startParts.year && !endParts.year) {
      console.log('No valid dates found, returning without year fields');
      return {
        eduId: null,
        schoolName: education.institution || '',
        degree: education.degree || '',
        speciality: education.field_of_study || '',
        street: '',
        city: '',
        state: '',
        country: 'US',
        zipcode: '',
        currentlyPursued: false, // boolean, not null
        institutionId: null,
        showThisOnProfile: true, // boolean, not null
        candidateId: candidateId,
        streamAction: 'ADD'
        // Don't include year fields if not available - API should not update them
      };
    }
    
    // Only validate if we have years
    const startYear = startParts.year ? this.validateEducationYearKeepEmpty(startParts.year) : null;
    const endYear = endParts.year ? this.validateEducationYearKeepEmpty(endParts.year) : null;
    
    console.log('Validated years:', { startYear, endYear });
    
    // Build the return object conditionally
    // Match EXACTLY with CandidateEducationDetails entity fields and types
    const result: any = {
      eduId: null,
      schoolName: education.institution || '',
      degree: education.degree || '',
      speciality: education.field_of_study || '',
      street: '',
      city: '',
      state: '',
      country: 'US',
      zipcode: '',
      currentlyPursued: false, // boolean, not null
      institutionId: null,
      showThisOnProfile: true, // boolean, not null
      candidateId: candidateId,
      streamAction: 'ADD'
    };
    
    // Only add date fields if they have values
    // This ensures API doesn't update fields when values are not available
    if (startParts.month) {
      result.startMonth = startParts.month;
    }
    if (startYear) {
      result.startYear = String(startYear); // Ensure String type
    }
    if (endParts.month) {
      result.endMonth = endParts.month;
    }
    if (endYear) {
      result.endYear = String(endYear); // Ensure String type
    } else if (startYear && education.degree) {
      // Only calculate end year if we have start year
      const duration = this.getEducationDuration(education.degree);
      const calculatedEndYear = parseInt(String(startYear)) + duration;
      result.endYear = String(calculatedEndYear); // Ensure String type
    }
    
    console.log('Final education object for backend:', result);
    
    return result;
  }

  /**
   * Formats certification for saving to backend
   */
  formatCertificationForBackend(certification: any, candidateId: string): any {
    // Parse dates with better handling
    const dateParts = certification.date 
      ? this.parseDateString(certification.date)
      : certification.issue_date 
        ? this.parseDateString(certification.issue_date)
        : { month: 'January', year: (new Date().getFullYear() - 1).toString() }; // Default to last year
    
    // Parse expiry date if available
    let expiryParts = null;
    if (certification.expiry_date) {
      expiryParts = this.parseDateString(certification.expiry_date);
    } else if (certification.expires) {
      expiryParts = this.parseDateString(certification.expires);
    }
    
    // Validate certification year
    const startYear = this.validateCertificationYear(dateParts.year);
    const endYear = expiryParts ? this.validateCertificationYear(expiryParts.year) : null;
    
    // Match EXACTLY with CandidateCertifications entity fields and types
    return {
      certificateId: null,
      certificationName: certification.name || certification.title || '',
      certificateOrganization: certification.issuer || certification.organization || '',
      certificateLicenseNo: certification.credential_id || certification.license_number || '',
      startMonth: dateParts.month,
      startYear: startYear ? String(startYear) : '', // Ensure String type
      endMonth: expiryParts?.month || null,
      endYear: endYear ? String(endYear) : null, // Ensure String type
      candidateId: candidateId,
      certificateNameId: null,
      streamAction: 'ADD'
    };
  }

  private parseDateString(dateStr: string): { month: string; year: string } {
    if (!dateStr || dateStr === '') {
      return { month: 'January', year: '' }; // Return empty year instead of current year
    }
    
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    
    // Clean the date string
    dateStr = dateStr.trim();
    
    // Handle "Present" or "Current"
    if (dateStr.toLowerCase() === 'present' || dateStr.toLowerCase() === 'current') {
      return { month: months[new Date().getMonth()], year: new Date().getFullYear().toString() };
    }
    
    // Try different date formats
    // Format: YYYY-MM-DD or YYYY-MM
    if (dateStr.match(/^\d{4}-\d{1,2}(-\d{1,2})?$/)) {
      const parts = dateStr.split('-');
      const year = parts[0];
      const monthNum = parseInt(parts[1]) - 1;
      return {
        month: months[monthNum] || 'January',
        year: year
      };
    }
    
    // Format: MM/DD/YYYY or MM/YYYY
    if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$|^\d{1,2}\/\d{4}$/)) {
      const parts = dateStr.split('/');
      const monthNum = parseInt(parts[0]) - 1;
      const year = parts.length > 2 ? parts[2] : parts[1];
      return {
        month: months[monthNum] || 'January',
        year: year
      };
    }
    
    // Format: Month YYYY (e.g., "January 2020", "Jan 2020")
    const monthYearMatch = dateStr.match(/^([A-Za-z]+)\s+(\d{4})$/);
    if (monthYearMatch) {
      const monthStr = monthYearMatch[1].toLowerCase();
      const year = monthYearMatch[2];
      
      for (let i = 0; i < months.length; i++) {
        if (months[i].toLowerCase().startsWith(monthStr.substring(0, 3))) {
          return {
            month: months[i],
            year: year
          };
        }
      }
    }
    
    // Format: YYYY only
    if (dateStr.match(/^\d{4}$/)) {
      return {
        month: 'January',
        year: dateStr
      };
    }
    
    // Try to extract year and month from text (e.g., "Graduated in May 2018")
    const yearMatch = dateStr.match(/\d{4}/);
    if (yearMatch) {
      let foundMonth = 'January';
      
      for (let i = 0; i < months.length; i++) {
        if (dateStr.toLowerCase().includes(months[i].toLowerCase().substring(0, 3))) {
          foundMonth = months[i];
          break;
        }
      }
      
      return {
        month: foundMonth,
        year: yearMatch[0]
      };
    }
    
    // If no valid year found, return empty
    return { month: 'January', year: '' };
  }
  
  // Helper method to estimate education start date based on degree and other context
  private estimateEducationStartDate(education: any): { month: string; year: string } {
    const currentYear = new Date().getFullYear();
    const degree = (education.degree || '').toLowerCase();
    
    // Try to extract year from any field
    const allText = JSON.stringify(education);
    const yearMatch = allText.match(/\b(19|20)\d{2}\b/g);
    
    if (yearMatch && yearMatch.length > 0) {
      // Use the earliest year found
      const years = yearMatch.map(y => parseInt(y)).sort();
      return { month: 'September', year: years[0].toString() };
    }
    
    // Default based on degree type
    if (degree.includes('phd') || degree.includes('doctorate')) {
      return { month: 'September', year: (currentYear - 8).toString() };
    } else if (degree.includes('master') || degree.includes('mba')) {
      return { month: 'September', year: (currentYear - 6).toString() };
    } else if (degree.includes('bachelor')) {
      return { month: 'September', year: (currentYear - 5).toString() };
    }
    
    return { month: 'September', year: (currentYear - 4).toString() };
  }
  
  // Helper method to estimate education end date
  private estimateEducationEndDate(education: any, startParts: { month: string; year: string }): { month: string; year: string } {
    const degree = (education.degree || '').toLowerCase();
    const startYear = parseInt(startParts.year) || new Date().getFullYear() - 4;
    
    // Duration based on degree type
    let duration = 4; // Default bachelor's
    if (degree.includes('associate')) {
      duration = 2;
    } else if (degree.includes('master') || degree.includes('mba')) {
      duration = 2;
    } else if (degree.includes('phd') || degree.includes('doctorate')) {
      duration = 4;
    }
    
    return { 
      month: 'May', 
      year: (startYear + duration).toString() 
    };
  }
  
  // New method: Estimate education start date with index consideration
  private estimateEducationStartDateWithIndex(education: any, index: number, allEducation: any[]): { month: string; year: string } {
    const currentYear = new Date().getFullYear();
    const degree = (education.degree || '').toLowerCase();
    const institution = (education.institution || '').toLowerCase();
    
    // Check if it's high school (XII, X, 12th, 10th, High School, Secondary)
    const isHighSchool = degree.includes('xii') || degree.includes('12') || 
                         degree.includes('x') || degree.includes('10') || 
                         degree.includes('high') || degree.includes('secondary') ||
                         institution.includes('school');
    
    // Check if it's college/university
    const isCollege = degree.includes('be') || degree.includes('bachelor') || 
                      degree.includes('btech') || degree.includes('bsc') ||
                      degree.includes('master') || degree.includes('mba') ||
                      degree.includes('phd') || institution.includes('college') ||
                      institution.includes('university');
    
    // Try to extract year from the education object
    const allText = JSON.stringify(education);
    const yearMatch = allText.match(/\b(19|20)\d{2}\b/g);
    
    if (yearMatch && yearMatch.length > 0) {
      const years = yearMatch.map(y => parseInt(y)).sort();
      // For high school, use earlier year; for college, use later year
      if (isHighSchool && index > 0) {
        return { month: 'June', year: Math.min(...years).toString() };
      }
      return { month: 'September', year: years[0].toString() };
    }
    
    // Estimate based on education level and index
    if (isHighSchool) {
      // High school typically ends 4-5 years before current year for recent graduates
      const hsStartYear = currentYear - 7; // Assuming they started high school 7 years ago
      return { month: 'June', year: hsStartYear.toString() };
    } else if (isCollege) {
      // College starts after high school
      if (degree.includes('phd') || degree.includes('doctorate')) {
        return { month: 'September', year: (currentYear - 4).toString() };
      } else if (degree.includes('master') || degree.includes('mba') || degree.includes('mtech')) {
        return { month: 'September', year: (currentYear - 3).toString() };
      } else if (degree.includes('bachelor') || degree.includes('be') || degree.includes('btech')) {
        return { month: 'September', year: (currentYear - 5).toString() };
      }
    }
    
    // Default: space out education entries by 4 years
    const baseYear = currentYear - 4 - (index * 4);
    return { month: 'September', year: baseYear.toString() };
  }
  
  // New method: Estimate education end date with index consideration
  private estimateEducationEndDateWithIndex(education: any, startParts: { month: string; year: string }, index: number): { month: string; year: string } {
    const degree = (education.degree || '').toLowerCase();
    const institution = (education.institution || '').toLowerCase();
    const startYear = parseInt(startParts.year) || new Date().getFullYear() - 4;
    
    // Check if it's high school
    const isHighSchool = degree.includes('xii') || degree.includes('12') || 
                         degree.includes('x') || degree.includes('10') || 
                         degree.includes('high') || degree.includes('secondary') ||
                         institution.includes('school');
    
    // Duration based on education type
    let duration = 4; // Default
    
    if (isHighSchool) {
      // High school is typically 2 years for senior secondary (XI-XII)
      if (degree.includes('xii') || degree.includes('12')) {
        duration = 2;
      } else if (degree.includes('x') || degree.includes('10')) {
        duration = 2;
      } else {
        duration = 4; // Full high school
      }
    } else if (degree.includes('phd') || degree.includes('doctorate')) {
      duration = 4;
    } else if (degree.includes('master') || degree.includes('mba') || degree.includes('mtech')) {
      duration = 2;
    } else if (degree.includes('bachelor') || degree.includes('be') || degree.includes('btech') || degree.includes('bsc')) {
      duration = 4;
    } else if (degree.includes('associate')) {
      duration = 2;
    } else if (degree.includes('diploma')) {
      duration = 2;
    }
    
    return { 
      month: isHighSchool ? 'May' : 'May', 
      year: (startYear + duration).toString() 
    };
  }
  
  // New method: Get education duration based on degree
  private getEducationDuration(degree: string): number {
    if (!degree) return 4;
    
    const degreeLower = degree.toLowerCase();
    
    if (degreeLower.includes('xii') || degreeLower.includes('12') || 
        degreeLower.includes('x') || degreeLower.includes('10')) {
      return 2;
    } else if (degreeLower.includes('associate') || degreeLower.includes('diploma')) {
      return 2;
    } else if (degreeLower.includes('master') || degreeLower.includes('mba') || degreeLower.includes('mtech')) {
      return 2;
    } else if (degreeLower.includes('bachelor') || degreeLower.includes('be') || 
               degreeLower.includes('btech') || degreeLower.includes('bsc')) {
      return 4;
    } else if (degreeLower.includes('phd') || degreeLower.includes('doctorate')) {
      return 4;
    }
    
    return 4; // Default
  }
  
  // Calculate education dates based on first job start year
  private calculateEducationDatesFromJobStart(education: any, firstJobYear: number, index: number, allEducation: any[]): 
    { start: { month: string; year: string }, end: { month: string; year: string } } {
    
    console.log('calculateEducationDatesFromJobStart - Input:', {
      degree: education.degree,
      institution: education.institution,
      firstJobYear,
      index
    });
    
    const degree = (education.degree || '').toLowerCase();
    const institution = (education.institution || '').toLowerCase();
    
    // Check education type
    const isHighSchool = degree.includes('xii') || degree.includes('12') || 
                         degree.includes('x') || degree.includes('10') || 
                         degree.includes('high') || degree.includes('secondary') ||
                         institution.includes('school');
    
    const isMasters = degree.includes('master') || degree.includes('mba') || degree.includes('mtech') || 
                      degree.includes('m.tech') || degree.includes('ms') || degree.includes('m.sc');
    
    const isBachelors = degree.includes('bachelor') || degree.includes('be') || degree.includes('btech') || 
                        degree.includes('b.tech') || degree.includes('bsc') || degree.includes('b.sc') || 
                        degree.includes('ba') || degree.includes('bba') || degree.includes('bcom');
    
    console.log('Education type detection:', { isHighSchool, isMasters, isBachelors });
    
    let startYear: number;
    let endYear: number;
    
    if (isHighSchool) {
      // School ends before college starts
      // If first job in 2017, college ended in 2016, so school ended in 2011 (before 4-year college + 1 year gap)
      if (degree.includes('xii') || degree.includes('12')) {
        // XII ends just before college starts
        // First job 2017 → College ends 2016 → School XII ends 2011 (5 years before: 4 college + 1 gap)
        endYear = firstJobYear - 6; // College 4 years + gap 1 year + 1 year before college
        startYear = endYear - 2; // XII is 2 years (11th and 12th)
      } else if (degree.includes('x') || degree.includes('10')) {
        // 10th standard - ends before XII
        endYear = firstJobYear - 8; // Before XII
        startYear = endYear - 2; // 9th and 10th
      } else {
        // General high school
        endYear = firstJobYear - 6;
        startYear = endYear - 4;
      }
    } else if (isMasters) {
      // Masters degree - done after bachelors
      // If first job in 2017 and did masters, it ended in 2016
      endYear = firstJobYear - 1; // Completed just before job
      startYear = endYear - 2; // Masters is typically 2 years
    } else if (isBachelors) {
      // Bachelor's degree
      // If first job in 2017, bachelor's ended in 2016
      endYear = firstJobYear - 1; // Completed 1 year before job
      startYear = endYear - 4; // Bachelor's is typically 4 years
      
      // If there's a master's degree in the list, adjust bachelor's to end earlier
      const hasMasters = allEducation.some(e => {
        const d = (e.degree || '').toLowerCase();
        return d.includes('master') || d.includes('mba') || d.includes('mtech');
      });
      
      if (hasMasters) {
        endYear = firstJobYear - 3; // 1 year before job - 2 years masters
        startYear = endYear - 4;
      }
    } else if (degree.includes('diploma')) {
      // Diploma courses
      endYear = firstJobYear - 1;
      startYear = endYear - 2; // Diploma is typically 2-3 years
    } else {
      // Default calculation for unknown degrees
      endYear = firstJobYear - 1 - (index * 2);
      startYear = endYear - 3;
    }
    
    console.log('Calculated years:', { startYear, endYear });
    
    return {
      start: { month: '', year: startYear.toString() },
      end: { month: '', year: endYear.toString() }
    };
  }
  
  // Validate education year but keep empty if not provided
  private validateEducationYearKeepEmpty(year: string): string | null {
    if (!year || year === '') {
      return null; // Keep empty instead of defaulting
    }
    
    const yearNum = parseInt(year);
    const currentYear = new Date().getFullYear();
    
    // Check if year is reasonable (between 1950 and current year + 5)
    if (yearNum < 1950) {
      return null; // Return null for unreasonable dates
    } else if (yearNum > currentYear + 5) {
      return currentYear.toString(); // Cap at current year for future dates
    }
    
    return year;
  }
  
  // Validate education year is reasonable (old method kept for compatibility)
  private validateEducationYear(year: string): string {
    if (!year || year === '') {
      return (new Date().getFullYear() - 4).toString(); // Default to 4 years ago
    }
    
    const yearNum = parseInt(year);
    const currentYear = new Date().getFullYear();
    
    // Check if year is reasonable (between 1950 and current year + 5)
    if (yearNum < 1950) {
      return '2000'; // Default for very old dates
    } else if (yearNum > currentYear + 5) {
      return currentYear.toString(); // Cap at current year for future dates
    }
    
    return year;
  }
  
  // Validate certification year
  private validateCertificationYear(year: string): string {
    if (!year || year === '') {
      return (new Date().getFullYear() - 1).toString(); // Default to last year
    }
    
    const yearNum = parseInt(year);
    const currentYear = new Date().getFullYear();
    
    // Check if year is reasonable (between 1990 and current year + 2)
    if (yearNum < 1990) {
      return '2020'; // Default for very old certifications
    } else if (yearNum > currentYear + 2) {
      return currentYear.toString(); // Cap at current year for future dates
    }
    
    return year;
  }
  
  // ============= ROBUST HELPER METHODS FOR 100+ RESUME FORMATS =============
  
  // Find personal info in various locations
  private findPersonalInfo(data: any): any {
    return data.personal_info || 
           data.personal || 
           data.contact || 
           data.basics || 
           data.candidate ||
           data.profile ||
           data || {};
  }
  
  // Find name with multiple fallbacks
  private findName(info: any, data: any): string | null {
    // Direct field names
    const nameFields = ['name', 'full_name', 'fullName', 'fullname', 'candidateName', 'Name', 'NAME'];
    
    for (const field of nameFields) {
      if (info[field]) return info[field];
      if (data[field]) return data[field];
    }
    
    // Try first_name + last_name combination
    const firstName = info.first_name || info.firstName || data.first_name || data.firstName;
    const lastName = info.last_name || info.lastName || data.last_name || data.lastName;
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    }
    
    // Extract from text content
    return this.extractNameFromText(data);
  }
  
  // Extract name from text using patterns
  private extractNameFromText(data: any): string | null {
    const text = JSON.stringify(data);
    
    // Patterns to find names
    const patterns = [
      /(?:Name|NAME|name)[\s:]+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/,
      /(?:I am|I'm|My name is)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/,
      /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)\s*$/m
    ];
    
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  }
  
  // Find email with validation
  private findEmail(info: any, data: any): string | null {
    const emailFields = ['email', 'email_address', 'emailAddress', 'mail', 'e-mail', 'Email', 'EMAIL'];
    
    for (const field of emailFields) {
      if (info[field] && this.isValidEmail(info[field])) return info[field];
      if (data[field] && this.isValidEmail(data[field])) return data[field];
    }
    
    // Extract from text
    const text = JSON.stringify(data);
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
    const matches = text.match(emailRegex);
    
    if (matches) {
      // Filter out test emails
      const validEmails = matches.filter(email => 
        !email.includes('example.com') && 
        !email.includes('test.com') &&
        !email.includes('@placeholder') &&
        this.isValidEmail(email)
      );
      return validEmails[0] || null;
    }
    
    return null;
  }
  
  // Validate email format
  private isValidEmail(email: string): boolean {
    if (!email) return false;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }
  
  // Find phone number
  private findPhone(info: any, data: any): string | null {
    const phoneFields = ['phone', 'phone_number', 'phoneNumber', 'mobile', 'cell', 'telephone', 'contact_number', 'Phone', 'PHONE'];
    
    for (const field of phoneFields) {
      if (info[field]) return info[field];
      if (data[field]) return data[field];
    }
    
    // Extract from text
    return this.extractPhoneFromText(data);
  }
  
  // Extract phone from text
  private extractPhoneFromText(data: any): string | null {
    const text = JSON.stringify(data);
    
    // Multiple phone patterns
    const patterns = [
      /\+?1?\s?\(?\d{3}\)?\s?[-.]?\d{3}[-.]?\d{4}/g,
      /\d{3}[-.\s]\d{3}[-.\s]\d{4}/g,
      /\(\d{3}\)\s*\d{3}[-.]?\d{4}/g,
      /\+\d{1,3}\s?\d{4,14}/g
    ];
    
    for (const pattern of patterns) {
      const matches = text.match(pattern);
      if (matches) {
        const validPhone = matches.find(p => {
          const digits = p.replace(/\D/g, '');
          return digits.length >= 10 && digits.length <= 15;
        });
        if (validPhone) return validPhone;
      }
    }
    
    return null;
  }
  
  // Format phone number - return plain digits only as requested
  private formatPhone(phone: string): string | null {
    if (!phone) return null;
    
    // Remove ALL non-digit characters - user wants plain numbers only
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Validate length
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      // Still return digits only even if length is unusual
      return digitsOnly || phone;
    }
    
    // For US numbers with country code, remove the 1
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return digitsOnly.substring(1); // Remove country code 1
    }
    
    // Return plain digits - no formatting
    return digitsOnly;
  }
  
  // Find LinkedIn URL
  private findLinkedIn(info: any, data: any): string | null {
    const linkedInFields = ['linkedin', 'linkedIn', 'linkedin_url', 'linkedin_profile', 'LinkedIn'];
    
    for (const field of linkedInFields) {
      if (info[field]) return info[field];
      if (data[field]) return data[field];
    }
    
    // Check in social arrays
    if (data.social && Array.isArray(data.social)) {
      const linkedin = data.social.find(s => 
        s.network?.toLowerCase() === 'linkedin' || 
        s.url?.includes('linkedin')
      );
      if (linkedin?.url) return linkedin.url;
    }
    
    // Extract from text
    const text = JSON.stringify(data);
    const linkedinRegex = /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/[a-zA-Z0-9\-]+/gi;
    const match = text.match(linkedinRegex);
    return match ? match[0] : null;
  }
  
  // Find portfolio/GitHub URL
  private findPortfolio(info: any, data: any): string | null {
    const portfolioFields = ['portfolio', 'website', 'github', 'personal_website', 'Portfolio', 'GitHub'];
    
    for (const field of portfolioFields) {
      if (info[field]) return info[field];
      if (data[field]) return data[field];
    }
    
    // Extract GitHub from text
    const text = JSON.stringify(data);
    const githubRegex = /(?:https?:\/\/)?(?:www\.)?github\.com\/[a-zA-Z0-9\-]+/gi;
    const match = text.match(githubRegex);
    return match ? match[0] : null;
  }
  
  // Clean and format URL
  private cleanUrl(url: string): string {
    if (!url) return '';
    
    // Add protocol if missing
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      if (url.includes('linkedin.com') || url.includes('github.com')) {
        return 'https://' + url;
      }
      return url;
    }
    return url;
  }
}